import axios, { AxiosError } from 'axios';

/**
 * Error mapping configuration
 * Maps backend error messages to user-friendly messages
 */
const ERROR_MESSAGES: Record<string, string> = {
  'Cannot demote the last owner of a tenant':
    'Cannot change role: This is the last owner of the organization. Every organization must have at least one owner.',
  'User is not a member of tenant':
    'The user is not a member of this organization.',
  'Tenant not found':
    'Organization not found.',
  'User already exists in tenant':
    'This user is already a member of the organization.',
  'Invalid role':
    'Invalid role specified. Please select a valid role.',
  'Cannot remove the last owner':
    'Cannot remove: This is the last owner of the organization. Every organization must have at least one owner.',
  'Insufficient permissions':
    'You do not have permission to perform this action.',
  'Email already registered':
    'This email address is already registered.',
  'Invalid email format':
    'Please enter a valid email address.',
  'Tenant name already exists':
    'An organization with this name already exists.',
};

/**
 * Extract error message from axios error response
 */
function extractErrorMessage(error: AxiosError): string | null {
  // Check for error in response data (various formats)
  const data = error.response?.data;

  if (data) {
    // Format 1: {error: "message"}
    if (typeof data === 'object' && 'error' in data && typeof data.error === 'string') {
      return data.error;
    }

    // Format 2: {message: "message"}
    if (typeof data === 'object' && 'message' in data && typeof data.message === 'string') {
      return data.message;
    }

    // Format 3: {error: {message: "message"}}
    if (
      typeof data === 'object' &&
      'error' in data &&
      typeof data.error === 'object' &&
      data.error !== null &&
      'message' in data.error &&
      typeof data.error.message === 'string'
    ) {
      return data.error.message;
    }

    // Format 4: string response
    if (typeof data === 'string') {
      return data;
    }
  }

  return null;
}

/**
 * Parse error and return user-friendly message
 * 
 * @param error - Error object from API call (AxiosError or Error)
 * @param defaultMessage - Default message to show if no specific mapping found
 * @returns User-friendly error message
 */
export function parseErrorMessage(error: unknown, defaultMessage = 'An unexpected error occurred'): string {
  // Handle axios errors
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;

    // Extract backend error message
    const backendMessage = extractErrorMessage(axiosError);

    if (backendMessage) {
      // Check if we have a user-friendly message for this error
      const friendlyMessage = ERROR_MESSAGES[backendMessage];
      if (friendlyMessage) {
        return friendlyMessage;
      }

      // Check for partial matches (case-insensitive)
      const lowerBackendMessage = backendMessage.toLowerCase();
      for (const [key, value] of Object.entries(ERROR_MESSAGES)) {
        if (lowerBackendMessage.includes(key.toLowerCase())) {
          return value;
        }
      }

      // Return backend message if no mapping found
      return backendMessage;
    }

    // Handle HTTP status codes
    if (axiosError.response?.status === 401) {
      return 'Authentication failed. Please log in again.';
    }
    if (axiosError.response?.status === 403) {
      return 'You do not have permission to perform this action.';
    }
    if (axiosError.response?.status === 404) {
      return 'The requested resource was not found.';
    }
    if (axiosError.response?.status === 409) {
      return 'A conflict occurred. The resource may already exist.';
    }
    if (axiosError.response?.status && axiosError.response.status >= 500) {
      return 'A server error occurred. Please try again later.';
    }

    // Handle network errors
    if (axiosError.code === 'ECONNABORTED') {
      return 'Request timeout. Please check your connection and try again.';
    }
    if (axiosError.code === 'ERR_NETWORK') {
      return 'Network error. Please check your internet connection.';
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message || defaultMessage;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Fallback
  return defaultMessage;
}

/**
 * Check if error matches a specific backend error message
 * Useful for conditional logic based on error type
 * 
 * @param error - Error object
 * @param message - Backend error message to check for
 * @returns true if error matches the message
 */
export function isErrorType(error: unknown, message: string): boolean {
  if (axios.isAxiosError(error)) {
    const backendMessage = extractErrorMessage(error as AxiosError);
    if (backendMessage) {
      return backendMessage.toLowerCase().includes(message.toLowerCase());
    }
  }
  return false;
}

/**
 * Add a new error message mapping
 * Useful for extending error messages at runtime
 * 
 * @param backendMessage - Backend error message
 * @param friendlyMessage - User-friendly message
 */
export function addErrorMapping(backendMessage: string, friendlyMessage: string): void {
  ERROR_MESSAGES[backendMessage] = friendlyMessage;
}

/**
 * Get all error mappings
 * Useful for debugging or displaying available mappings
 */
export function getErrorMappings(): Record<string, string> {
  return { ...ERROR_MESSAGES };
}
